import { useFlag } from '@unleash/proxy-client-react';
import { InsuranceDetails, postCancellationCwp } from 'Api/insurance';
import {
  ProductName,
  ProductsWithCancelForm,
} from 'Constants/products.constants';
import { useFormikContext } from 'formik';
import { useFormStatus } from 'Forms/forms.hooks';
import { isUnderCancellation } from 'Utils/insuranceStatus/insuranceStatus.utils';
import { getCancelToggleName } from 'Utils/unleash/unleash.utils';
import { mapToCancellationRequest } from './cancelForm.mappers';
import {
  CancelFormStatus,
  CancelFormValues,
  ProductInsuranceCancelRequest,
  UseCancelFormReturn,
  UsePostInsuranceCancelReturn,
} from './cancelForm.types';

export const useCancelForm = (
  insurance: InsuranceDetails,
): UseCancelFormReturn => {
  const postCancellation = usePostCancellation();

  const handleSubmit = (
    values: CancelFormValues,
    setSubmitting: (isSubmitting: boolean) => void,
    setStatus: (status: CancelFormStatus) => void,
  ): void => {
    const mappedFormValues = mapToCancellationRequest(insurance, values);
    postCancellation(insurance, mappedFormValues, setSubmitting, setStatus);
  };

  return { handleSubmit };
};

const usePostCancellation = (): UsePostInsuranceCancelReturn => {
  return async (
    insurance: InsuranceDetails,
    mappedValues: ProductInsuranceCancelRequest,
    setSubmitting: (isSubmitting: boolean) => void,
    setStatus: (status: CancelFormStatus) => void,
  ) => {
    try {
      const { status } = await postCancellationCwp(insurance, mappedValues);
      const submitted = status === 200;
      setSubmitting(submitted);
      setStatus({ submit: 'success' });
    } catch (err) {
      setStatus({ submit: 'error' });
    }
  };
};

export const useIsInsuranceWithCancelForm = (product: ProductName): boolean => {
  const cancelToggleName = getCancelToggleName(product);
  const canUseNewCancelForm = useFlag(cancelToggleName ?? '');
  const hasNewCancelForm = ProductsWithCancelForm.includes(product);
  return canUseNewCancelForm && hasNewCancelForm;
};

export const useGetDisabledFields = (insurance: InsuranceDetails): boolean => {
  const { isSubmitting } = useFormikContext();
  const { submitSuccess } = useFormStatus();
  const { insuranceStatus } = insurance;
  const underResignation = isUnderCancellation(insuranceStatus);
  return isSubmitting || submitSuccess || underResignation;
};
